/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ClusterApiService } from '../../http';
import { ErrorResponseModel } from '../../../../../core/models';
import { Injectable, OnDestroy } from '@angular/core';
import { LanguageService, LogService, NotifyService, UserService, UtilsService } from '../../../../../core/services';
import { NodeModel } from '../../models';
import { ServiceModel } from '../../models/serviceModel';
import { Subject } from 'rxjs';

@Injectable()
export class PromptService implements OnDestroy {
    /**
     * States emitter to inform other components.
     */
    public serviceStatusEmitter: Subject<object> = new Subject<object>();

    /**
     * States emitter to inform other components.
     */
    public localServiceStatusEmitter: Subject<object> = new Subject<object>();

    /**
     * Store the node services
     * @private
     */
    private _nodeServiceStatuses: [] = [];

    /**
     * Store the local node services
     * @private
     */
    private _localNodeServices: ServiceModel[] = [];

    /**
     * Store the local node appliance ID.
     * @private
     */
    private _localNodeApplianceId: string = '';

    /**
     * Stored socket data for comparison.
     * @private
     */
    private storedSocketData: string;

    /**
     * Stored socket services data for comparison.
     * @private
     */
    private storedServicesSocketData: string;

    /**
     * Stored Timestamp of the last socket event.
     * @private
     */
    private timeStamp: number;

    /**
     * Stored intervalId for use in OnDestroy.
     * @private
     */
    private intervalId: any;

    constructor(
        private clusterApiService: ClusterApiService,
        private languageService: LanguageService,
        private logService: LogService,
        private notifyService: NotifyService,
        private utilsService: UtilsService,
        private userService: UserService
    ) {
        this.onInitialise();
        this.subscribeToUserService();
    }

    onInitialise(): void {
        this.timeStamp = new Date().getTime();
        this.intervalId = setInterval((): void => {
            if (Math.abs(this.timeStamp - new Date().getTime()) > 15000) {
                this.getDataFromApi();
                this.logService.debug('PromptService:onInitialise', 'Socket data out of date, making an API call.');
            }
        }, 15000);
    }

    ngOnDestroy(): void {
        clearInterval(this.intervalId);
    }

    /**
     * All data comes in here from the websocket subscriber on the loadbalancer component.
     * @param nodeServiceStatuses
     */
    set nodeServiceStatuses(nodeServiceStatuses: any) {
        this.timeStamp = new Date().getTime();
        // Only update everyone if the data has changed
        if (this.storedSocketData !== JSON.stringify(nodeServiceStatuses)) {
            this.storedSocketData = JSON.stringify(nodeServiceStatuses);
            this._nodeServiceStatuses = nodeServiceStatuses?.nodes;
            this.serviceStatusEmitter.next(this._nodeServiceStatuses);
            this.setUpLocalNodeDetails();
        }
    }

    get nodeServiceStatuses() {
        return this._nodeServiceStatuses;
    }

    set localNodeServices(localNodeServices: any) {
        if (this._localNodeServices.length) {
            this.localServiceStatusEmitter.next(localNodeServices);
            localStorage.setItem('lbServices', JSON.stringify(localNodeServices));
        }
    }

    get localNodeServices(): ServiceModel[] {
        return this._localNodeServices;
    }

    set localNodeApplianceId(localNodeId: any) {
        this._localNodeApplianceId = localNodeId;
    }

    get localNodeApplianceId(): any {
        return this._localNodeApplianceId;
    }

    /**
     * Initialise prompt service with API data
     * @param data
     * @param applianceId
     */
    public initialisePromptService(data: any[], applianceId: string): void {
        this._localNodeServices = data;
        this.localNodeApplianceId = applianceId;
    }

    /**
     * Set up local node details.
     */
    public setUpLocalNodeDetails(): void {
        if (this.localNodeApplianceId) {
            const localNodeStatuses: any = this.nodeServiceStatuses.find((item: any): boolean => item.id === this.localNodeApplianceId);
            if (localNodeStatuses?.services && this.storedServicesSocketData !== JSON.stringify(localNodeStatuses?.services)) {
                this.storedServicesSocketData = JSON.stringify(localNodeStatuses?.services);
                this.localNodeServices = localNodeStatuses.services;
            }

            this.notifyService.removeNotificationById('servicePrompt');
            if (this.hasServicesRequiringRestartReload()) {
                this.sendRestartReloadNotification();
            }
        }
    }

    /**
     * Check to see if any services require restart or reload.
     */
    public hasServicesRequiringRestartReload(): boolean {
        return this.localNodeServices.some((item: ServiceModel): boolean => item.action === 'restart' || item.action === 'reload');
    }

    /**
     * Tear down service.
     */
    public tearDownService(): void {
        clearInterval(this.intervalId);
    }

    /**
     * Send restart reload notification.
     * @private
     */
    private sendRestartReloadNotification(): void {
        const restartReloadService: any = this.localNodeServices.filter(
            (item: ServiceModel): boolean => item.action === 'restart' || item.action === 'reload'
        );
        if (restartReloadService.length) {
            const serviceNames: any = restartReloadService.map((item: ServiceModel): any => item.fullName).toString();
            this.setStaticNotification(serviceNames);
        }
    }

    /**
     * Set the static notifications based on the pending service changes.
     * @param serviceNames
     * @private
     */
    private setStaticNotification(serviceNames: string): void {
        const services: string = this.utilsService.replaceLastCommaWithAnd(serviceNames);
        const message: string = `
            ${this.languageService.translate('services_notification_changes')} <b>` + services + `.</b>
            <br><small>
            ${this.languageService.translate('services_notification_commit_message')}
            </small>
        `;

        const url: string = serviceNames
            ? 'maintenance/services?sFilter=' + serviceNames
            : 'maintenance/services';

        this.notifyService.info(message, {
            id: 'servicePrompt', static: true, hideTypeMessage: true, url: url, urlCloseOnClick: true
        });
    }

    /**
     * Get data from API.
     * @private
     */
    private getDataFromApi(): void {
        this.clusterApiService.getStatus()
            .subscribe({
                next: (data: any): void => {
                    this.nodeServiceStatuses = { nodes: this.formatNodeData(data.nodes) };
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PromptService:getDataFromApi', 'Unable to get data from API', error.message);
                }
            });
    }

    /**
     *
     * @param nodes
     * @private
     */
    private formatNodeData(nodes: NodeModel[]) {
        const formattedNodes: any = [];
        for (const node of nodes) {
            formattedNodes.push({
                id: node.id,
                services: node.services,
            });
        }

        return formattedNodes;
    }

    /**
     * Subscribe to user service to detect logout.
     * @private
     */
    private subscribeToUserService(): void {
        this.userService.logOutEmitter
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.tearDownService();
                    }
                }
            });
    }
}
